// Facturación en la app del chofer (decisión D57). Cuando la factura sale "al entregar" o "al cobrar", la app la arma
// en el momento, sin señal, con lo mismo que usa el servidor (@reparto/shared): el emisor y el punto de su móvil, el
// timbrado vigente, la tasa de cada artículo y la liquidación del IVA. El número sale de la reserva del día que bajó en
// el paquete (el servidor lo valida al sincronizar); sin reserva, o para SIFEN, el papel es un provisorio que se
// numera en el servidor y se reimprime después.
import {
  type ComprobanteImprimible,
  type CondicionVenta,
  condicionDeCobro,
  formatoNumeroComprobante,
  liquidarRenglones,
  type MedioCobro,
  type PlanFacturacion,
  siguienteDeReserva,
  type TasaIva,
  type TipoDocumentoFiscal,
  uuidv7,
} from '@reparto/shared/nucleo';
import { db } from './db';
import type { Parada } from './tipos';

/** Lo que baja en el paquete del día (`facturacion`). */
export interface PaqueteFacturacion {
  planes: Record<CondicionVenta, PlanFacturacion>;
  formato: 'ticket' | 'a4' | 'ambos';
  emisor_datos: ComprobanteImprimible['emisor_datos'];
  punto: { id: string; establecimiento: string; punto: string; nombre: string | null } | null;
  timbrados: Array<{
    id: string;
    numero: string;
    tipo: 'autoimpresor' | 'preimpreso' | 'electronico';
    vigencia_desde: string;
    vigencia_hasta: string | null;
    documentos: TipoDocumentoFiscal[];
  }>;
  reservas: Array<{
    id: string;
    tipo: TipoDocumentoFiscal;
    punto_expedicion_id: string;
    desde: number;
    hasta: number;
    usados: number[];
  }>;
  receptores: Record<string, ComprobanteImprimible['receptor']>;
  tasas: Record<string, TasaIva>;
  /** Los comprobantes que ya tienen los pedidos (el servidor manda: tienen el número definitivo). */
  comprobantes: Record<string, ComprobanteImprimible[]>;
}

/** Si el documento de la venta sale en esta entrega: "al entregar", o "al cobrar" con plata cobrada. */
export function saleAlEntregar(plan: PlanFacturacion, medio: MedioCobro, cobrado: number): boolean {
  if (plan.momento === 'entregar') return true;
  return plan.momento === 'cobrar' && medio !== 'cuenta_corriente' && cobrado > 0;
}

export function planDe(f: PaqueteFacturacion | undefined, cobro: MedioCobro): PlanFacturacion | null {
  return f?.planes?.[condicionDeCobro(cobro)] ?? null;
}

/** El timbrado vigente hoy que habilita el documento (solo autoimpresor si el documento es la factura autoimpresor). */
export function timbradoPara(
  f: PaqueteFacturacion,
  plan: PlanFacturacion,
  hoy: string,
): PaqueteFacturacion['timbrados'][number] | null {
  const tipos = plan.documento === 'autoimpresor' ? ['autoimpresor'] : ['autoimpresor', 'preimpreso'];
  return (
    f.timbrados
      .filter(
        (t) =>
          tipos.includes(t.tipo) &&
          t.documentos.includes(plan.tipo) &&
          t.vigencia_desde <= hoy &&
          (!t.vigencia_hasta || t.vigencia_hasta >= hoy),
      )
      .sort((a, b) => b.vigencia_desde.localeCompare(a.vigencia_desde))[0] ?? null
  );
}

export interface DocumentoArmado {
  /** Lo que va en la operación `entregar` (el servidor lo valida y lo numera si es provisorio). */
  documento: {
    comprobante_id: string;
    tipo: TipoDocumentoFiscal;
    reserva_id: string | null;
    numero: number | null;
  };
  comprobante: ComprobanteImprimible;
}

/**
 * Arma el documento de la entrega con lo entregado. Numera con la reserva si el emisor es nuestro sistema, el móvil
 * tiene su punto, hay reserva de ese documento con números libres y (salvo el interno) un timbrado vigente. Si no, sale
 * provisorio (sin número).
 */
export function armarDocumento(a: {
  f: PaqueteFacturacion;
  plan: PlanFacturacion;
  parada: Pick<Parada, 'pedido' | 'cliente' | 'items'>;
  cantidades: Record<string, number>;
  usadosLocales: Record<string, number[]>;
  hoy: string;
  ahora?: Date;
}): DocumentoArmado {
  const { f, plan, parada } = a;
  const ahora = (a.ahora ?? new Date()).toISOString();
  const id = uuidv7();
  const timbrado = plan.tipo === 'interno' ? null : timbradoPara(f, plan, a.hoy);
  const reserva =
    plan.emisor === 'local' && f.punto && (plan.tipo === 'interno' || timbrado)
      ? f.reservas.find(
          (r) =>
            r.tipo === plan.tipo &&
            r.punto_expedicion_id === f.punto!.id &&
            siguienteDeReserva(r, a.usadosLocales[r.id]) !== null,
        )
      : undefined;
  const numero = reserva ? siguienteDeReserva(reserva, a.usadosLocales[reserva.id]) : null;
  const items = parada.items
    .map((i) => ({
      codigo: i.codigo,
      descripcion: i.nombre,
      unidad: i.unidad,
      cantidad: a.cantidades[i.id] ?? 0,
      precio_unitario: i.precio_bulto,
      tasa_iva: f.tasas[i.articulo_id] ?? (10 as TasaIva),
    }))
    .filter((i) => i.cantidad > 0)
    .map((i) => ({ ...i, total: i.cantidad * i.precio_unitario }));
  const totales = liquidarRenglones(items.map((i) => ({ ...i, tasa: i.tasa_iva })));
  const comprobante: ComprobanteImprimible = {
    id,
    tipo: plan.tipo,
    estado: numero !== null ? 'emitido' : 'provisorio',
    emisor: plan.emisor,
    condicion: plan.condicion,
    numero_visible:
      numero !== null && f.punto ? formatoNumeroComprobante(f.punto.establecimiento, f.punto.punto, numero) : null,
    fecha_emision: numero !== null ? ahora : null,
    creado_en: ahora,
    timbrado:
      numero !== null && timbrado
        ? { numero: timbrado.numero, vigencia_desde: timbrado.vigencia_desde, vigencia_hasta: timbrado.vigencia_hasta }
        : null,
    emisor_datos: f.emisor_datos,
    receptor: f.receptores[parada.cliente.id] ?? { nombre: parada.cliente.nombre, razon_social: parada.cliente.nombre },
    items,
    totales,
    asociado: null,
    motivo: null,
    mensaje: null,
    transporte: null,
    pedido_numero_visible: parada.pedido.numero_visible,
    cdc: null,
  };
  return {
    documento: { comprobante_id: id, tipo: plan.tipo, reserva_id: numero !== null ? reserva!.id : null, numero },
    comprobante,
  };
}

/** Los números de la reserva que este celular ya usó y todavía no confirmó el servidor. */
export async function usadosLocales(): Promise<Record<string, number[]>> {
  const filas = await db.reservasUsadas.toArray();
  return Object.fromEntries(filas.map((f) => [f.id, f.usados]));
}

/** Guarda el documento armado: el número queda usado y el comprobante, para reimprimirlo. */
export async function guardarDocumento(pedidoId: string, d: DocumentoArmado): Promise<void> {
  await db.transaction('rw', [db.reservasUsadas, db.comprobantes], async () => {
    if (d.documento.reserva_id && d.documento.numero !== null) {
      const previa = await db.reservasUsadas.get(d.documento.reserva_id);
      await db.reservasUsadas.put({
        id: d.documento.reserva_id,
        usados: [...new Set([...(previa?.usados ?? []), d.documento.numero])],
      });
    }
    await db.comprobantes.put({ pedido_id: pedidoId, comprobante: d.comprobante, local: true });
  });
}

/** El servidor devolvió el documento definitivo de una entrega: reemplaza al que armó el celular. */
export async function guardarDelServidor(pedidoId: string, c: ComprobanteImprimible): Promise<void> {
  await db.comprobantes.put({ pedido_id: pedidoId, comprobante: c, local: false });
}

/**
 * El documento para reimprimir de un pedido: el del servidor (en el paquete o devuelto al sincronizar) si hay; si no,
 * el que armó el celular. Solo los de venta (factura, nota de venta o interno) que no estén anulados.
 */
export function documentoDePedido(
  pedidoId: string,
  f: PaqueteFacturacion | undefined,
  local: { comprobante: ComprobanteImprimible; local: boolean } | undefined,
): ComprobanteImprimible | null {
  const venta = (c: ComprobanteImprimible) =>
    (c.tipo === 'factura' || c.tipo === 'nota_venta' || c.tipo === 'interno') && c.estado !== 'anulado';
  const delServidor = (f?.comprobantes?.[pedidoId] ?? []).find(venta);
  if (delServidor) return delServidor;
  if (local && !local.local) return local.comprobante;
  return local?.comprobante ?? null;
}
